/** @prettier */

import {Box, IconButton} from '@mui/material';
import {useTranslation} from 'react-i18next';

import {iconBlack, iconWhite} from '../../define';

/**
 * グラフボタン（フロア、グループ、空調機専用）
 * @param props props
 * - id id
 * - isHover ホバーされているかどうか
 * - setHoveringIcon ホバーされているアイコンボタンの種類のset関数
 * - onClick 押下時の処理
 * - isIconDisabled trueの場合アイコンを無効化
 * @returns JSX
 */
const GraphButton = (props: {
  id?: string;
  isHover: boolean;
  setHoveringIcon: React.Dispatch<
    React.SetStateAction<'' | 'graph' | 'control'>
  >;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  isIconDisabled: boolean;
}) => {
  const trans = useTranslation().t; //翻訳関数

  return (
    <IconButton
      id={props.id}
      size="small"
      onClick={props.onClick}
      disabled={props.isIconDisabled}
      className={
        props.isIconDisabled
          ? 'subContents__content__body__data__powerGraphOpe__graphOpeIcon__icon--disabled'
          : ''
      }>
      <Box
        className="subContents__content__body__data__powerGraphOpe__graphOpeIcon__icon__button"
        onMouseEnter={() => props.setHoveringIcon('graph')}
        onMouseLeave={() => props.setHoveringIcon('')}>
        <img
          title={`${trans(
            `SiteSubContentsIconButton.image.title.${iconBlack.BAR_GRAPH.title}`
          )}`}
          src={
            props.isHover ? iconWhite.BAR_GRAPH.src : iconBlack.BAR_GRAPH.src
          }
          alt={
            props.isHover
              ? `${trans(
                  `SiteSubContentsIconButton.image.alternativeText.${iconWhite.BAR_GRAPH.alt}`
                )}`
              : `${trans(
                  `SiteSubContentsIconButton.image.alternativeText.${iconBlack.BAR_GRAPH.alt}`
                )}`
          }
          className="subContents__content__body__data__powerGraphOpe__graphOpeIcon__icon__button__image"
        />
      </Box>
    </IconButton>
  );
};

/**
 * 機器操作ボタン（フロア、グループ、空調機専用）
 * @param props props
 * @returns JSX.Element
 */
const ControlButton = (props: {
  /** id */
  id?: string;
  /** ホバーされているかどうか */
  isHover: boolean;
  /** ホバーされているアイコンボタンの種類のset関数 */
  setHoveringIcon: React.Dispatch<
    React.SetStateAction<'' | 'graph' | 'control'>
  >;
  /** 押下時の処理 */
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  /** アイコンを無効化するかどうか */
  isIconDisabled?: boolean;
}) => {
  const trans = useTranslation().t; //翻訳関数

  return (
    <IconButton
      id={props.id}
      size="small"
      onClick={props.onClick}
      disabled={props.isIconDisabled}
      className={
        props.isIconDisabled
          ? 'subContents__content__body__data__powerGraphOpe__graphOpeIcon__icon--disabled'
          : ''
      }>
      <Box
        className="subContents__content__body__data__powerGraphOpe__graphOpeIcon__icon__button"
        onMouseEnter={() => props.setHoveringIcon('control')}
        onMouseLeave={() => props.setHoveringIcon('')}>
        <img
          title={`${trans(
            `SiteSubContentsIconButton.image.title.${iconBlack.CONTROLLER.title}`
          )}`}
          src={
            props.isHover ? iconWhite.CONTROLLER.src : iconBlack.CONTROLLER.src
          }
          alt={
            props.isHover
              ? `${trans(
                  `SiteSubContentsIconButton.image.alternativeText.${iconWhite.CONTROLLER.alt}`
                )}`
              : `${trans(
                  `SiteSubContentsIconButton.image.alternativeText.${iconBlack.CONTROLLER.alt}`
                )}`
          }
          className="subContents__content__body__data__powerGraphOpe__graphOpeIcon__icon__button__image"
        />
      </Box>
    </IconButton>
  );
};

/**
 * 棒グラフボタン（熱源機専用）
 * @param props props
 * - id id
 * - isHover ホバーされているかどうか
 * - setHoveringIcon ホバーされているアイコンボタンの種類のset関数
 * - onClick 押下時の処理
 * - isIconDisabled trueの場合アイコンを無効化
 * @returns JSX
 */
const BarGraphButton = (props: {
  id?: string;
  isHover: boolean;
  setHoveringIcon: React.Dispatch<React.SetStateAction<'' | 'bar' | 'line'>>;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  isIconDisabled: boolean;
}) => {
  const trans = useTranslation().t; //翻訳関数

  return (
    <IconButton
      size="small"
      onClick={props.onClick}
      disabled={props.isIconDisabled}
      className={
        props.isIconDisabled
          ? 'subContents__content__body__data__powerGraphOpe__graphOpeIcon__icon--disabled'
          : ''
      }>
      <Box
        className="subContents__content__body__data__powerGraphOpe__graphOpeIcon__icon__button"
        onMouseEnter={() => props.setHoveringIcon('bar')}
        onMouseLeave={() => props.setHoveringIcon('')}>
        <img
          title={`${trans(
            `SiteSubContentsIconButton.image.title.${iconBlack.BAR_GRAPH.title}`
          )}`}
          src={
            props.isHover ? iconWhite.BAR_GRAPH.src : iconBlack.BAR_GRAPH.src
          }
          alt={
            props.isHover
              ? `${trans(
                  `SiteSubContentsIconButton.image.alternativeText.${iconWhite.BAR_GRAPH.alt}`
                )}`
              : `${trans(
                  `SiteSubContentsIconButton.image.alternativeText.${iconBlack.BAR_GRAPH.alt}`
                )}`
          }
          className="subContents__content__body__data__powerGraphOpe__graphOpeIcon__icon__button__image"
        />
      </Box>
    </IconButton>
  );
};

/**
 * 折れ線グラフボタン（熱源機専用）
 * @param props props
 * - id id
 * - isHover ホバーされているかどうか
 * - setHoveringIcon ホバーされているアイコンボタンの種類のset関数
 * - onClick 押下時の処理
 * - isIconDisabled trueの場合アイコンを無効化
 * @returns JSX
 */
const LineGraphButton = (props: {
  id?: string;
  isHover: boolean;
  setHoveringIcon: React.Dispatch<React.SetStateAction<'' | 'bar' | 'line'>>;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  isIconDisabled: boolean;
}) => {
  const trans = useTranslation().t; //翻訳関数

  return (
    <IconButton
      size="small"
      onClick={props.onClick}
      disabled={props.isIconDisabled}
      className={
        props.isIconDisabled
          ? 'subContents__content__body__data__powerGraphOpe__graphOpeIcon__icon--disabled'
          : ''
      }>
      <Box
        className="subContents__content__body__data__powerGraphOpe__graphOpeIcon__icon__button"
        onMouseEnter={() => props.setHoveringIcon('line')}
        onMouseLeave={() => props.setHoveringIcon('')}>
        <img
          title={`${trans(
            `SiteSubContentsIconButton.image.title.${iconBlack.LINE_GRAPH.title}`
          )}`}
          src={
            props.isHover ? iconWhite.LINE_GRAPH.src : iconBlack.LINE_GRAPH.src
          }
          alt={
            props.isHover
              ? `${trans(
                  `SiteSubContentsIconButton.image.alternativeText.${iconWhite.LINE_GRAPH.alt}`
                )}`
              : `${trans(
                  `SiteSubContentsIconButton.image.alternativeText.${iconBlack.LINE_GRAPH.alt}`
                )}`
          }
          className="subContents__content__body__data__powerGraphOpe__graphOpeIcon__icon__button__image"
        />
      </Box>
    </IconButton>
  );
};

export {GraphButton, ControlButton, BarGraphButton, LineGraphButton};
